'use client';

import { useEffect, useRef, useState } from 'react';

import { useLazyQuery } from '@apollo/client/react';

import { FORMAT_TRANSCRIPT_ANSWER, QuestionType } from '@/survey-graphql';

interface Option {
  label: string;
  value: string;
}

const TRANSCRIPT_SILENCE_FORMAT_DELAY_MS = 1000;

const OPTION_EXTRACTION_QUESTION_TYPES = new Set<QuestionType>([
  QuestionType.MultipleChoice,
  QuestionType.Rating,
]);

export function supportsRealtimeVoiceMatchQuestionType(
  questionType: QuestionType
) {
  return OPTION_EXTRACTION_QUESTION_TYPES.has(questionType);
}

/**
 * Silence-based real-time voice matcher. Fires the `formatTranscriptAnswer` AI
 * query after one second without transcript changes (while `enabled`) and
 * returns the comma-joined `extractedOptions` string - the same encoding as the
 * `tentativeValue` prop used by selection components.
 *
 * `fetchPolicy: 'cache-first'` means the cache is pre-warmed, so when the
 * user says "Next" and the handler calls the same query, it resolves
 * instantly from cache with no extra network round-trip.
 */
export function useRealtimeVoiceMatch({
  transcript,
  options,
  maxSelections,
  questionType,
  enabled,
  questionId,
  preserveMatchOnDisable = false,
}: {
  transcript: string;
  options: Option[];
  maxSelections?: number | null;
  questionType: QuestionType;
  enabled: boolean;
  questionId?: string;
  preserveMatchOnDisable?: boolean;
}): string {
  const trimmedTranscript = transcript.trim();
  const supportedQuestionType =
    supportsRealtimeVoiceMatchQuestionType(questionType);
  const canMatch =
    enabled &&
    Boolean(trimmedTranscript) &&
    Boolean(options.length) &&
    supportedQuestionType;
  const shouldPreserveSticky = !enabled && preserveMatchOnDisable;
  const [matchState, setMatchState] = useState({
    questionId,
    matched: '',
    sticky: '',
  });
  const requestIdRef = useRef(0);

  // Adjust state while rendering so prop-driven resets do not add an effect
  // render pass. This keeps stale highlights out of new/disabled questions.
  if (matchState.questionId !== questionId) {
    setMatchState({ questionId, matched: '', sticky: '' });
  } else if (!canMatch) {
    if (shouldPreserveSticky && matchState.matched) {
      setMatchState((prev) => ({ ...prev, matched: '' }));
    } else if (
      !shouldPreserveSticky &&
      (matchState.matched || matchState.sticky)
    ) {
      setMatchState((prev) => ({ ...prev, matched: '', sticky: '' }));
    }
  }

  // Refs so the debounce closure always reads the freshest values without
  // putting them in the dep array - prevents reference-churn from
  // resetting the debounce on every parent re-render.
  const optionsRef = useRef(options);
  const questionTypeRef = useRef(questionType);
  const maxSelectionsRef = useRef(maxSelections);

  useEffect(() => {
    optionsRef.current = options;
    questionTypeRef.current = questionType;
    maxSelectionsRef.current = maxSelections;
  });

  const [runQuery] = useLazyQuery(FORMAT_TRANSCRIPT_ANSWER, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  // Silence-window AI call. Only transcript/enabled/question identity restart
  // the timer - options/questionType/maxSelections are read from refs above.
  useEffect(() => {
    if (!canMatch || !questionId) return;

    let cancelled = false;
    const requestId = ++requestIdRef.current;
    const id = setTimeout(async () => {
      const opts = optionsRef.current;
      const currentQuestionType = questionTypeRef.current;
      if (
        !opts.length ||
        !supportsRealtimeVoiceMatchQuestionType(currentQuestionType)
      ) {
        return;
      }
      const { data } = await runQuery({
        variables: {
          input: {
            questionType: currentQuestionType,
            options: opts,
            transcript: trimmedTranscript,
          },
        },
      });
      if (cancelled || requestId !== requestIdRef.current) return;
      const list = data?.formattedTranscriptAnswer?.extractedOptions;
      if (!list?.length) {
        setMatchState((prev) => ({ ...prev, matched: '' }));
        return;
      }
      const ms = maxSelectionsRef.current;
      const result = (ms ? list.slice(0, ms) : list).join(',');
      setMatchState((prev) => ({
        ...prev,
        matched: result,
        sticky: result,
      }));
    }, TRANSCRIPT_SILENCE_FORMAT_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [canMatch, trimmedTranscript, questionId, runQuery]);

  // Live match wins; sticky is only used by callers that explicitly preserve
  // during a transient disabled state such as submit.
  return (
    matchState.matched ||
    (enabled || preserveMatchOnDisable ? matchState.sticky : '')
  );
}
